/**
 * Lesetext mit char-by-char-Streaming (1:1 zur Web-App, app.js streamHtmlInto):
 *  - Endhöhe vorab reservieren (Box springt nicht),
 *  - Zeichen einzeln; Block-Elemente (table/ul/ol/blockquote/pre/figure/Medien) am Stück,
 *  - prefers-reduced-motion → sofort voll anzeigen,
 *  - Geschwindigkeit aus localStorage `ki-kurs-stream-speed` (Default 95 cps).
 */
import { useEffect, useRef } from "preact/hooks";
import { sendProgressEvent } from "../state/progress";
import { filterTrainerHtml } from "./trainer-filter";
import { sanitizeHtml } from "./sanitize";
import { handleLessonClick } from "./lesson-interactions";
import { askQuestion, insertIntoCoach, openPromptDialog } from "../state/coach";
import { showToast } from "../ui/toast";

interface Ctx {
  courseId: string;
  moduleId: string;
  blockId: string;
}

const BLOCK_AT_ONCE = ["table", "ul", "ol", "blockquote", "pre", "figure", "img", "video", "audio", "iframe"];
// Interaktive/strukturierte Blöcke am Stück einfügen, damit Buttons/Karusselle
// nicht halbfertig anklickbar werden (Codex-Finding: Streaming-Atomarität).
const ATOMIC_CLASSES = ["ask-ki", "step-carousel", "step-stapel", "anbieter-card", "prompt-box", "wk-material"];

function isAtomic(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (BLOCK_AT_ONCE.includes(tag)) return true;
  return ATOMIC_CLASSES.some((c) => el.classList.contains(c));
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}
/** Tempo 1:1 zum Web (app.js streamSpeed): Default 320 Zeichen/s, Clamp 40–1200,
 *  12-ms-Ticks mit mehreren Zeichen pro Tick (chunk) statt Zeichen-einzeln. */
function streamSpeed(speed?: number): { delay: number; chunk: number } {
  const raw = Number(localStorage.getItem("ki-kurs-stream-speed")) || speed || 320;
  const cps = Math.max(40, Math.min(1200, raw));
  const delay = 12;
  const chunk = Math.max(1, Math.round((cps * delay) / 1000));
  return { delay, chunk };
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function cloneShallow(node: Node): Node {
  if (node.nodeType === Node.TEXT_NODE) return document.createTextNode("");
  return node.cloneNode(false);
}

async function streamNode(
  source: Node,
  target: Node,
  abort: { aborted: boolean },
  sp: { delay: number; chunk: number },
) {
  if (abort.aborted) return;
  if (source.nodeType === Node.TEXT_NODE) {
    const text = source.textContent || "";
    for (let i = 0; i < text.length; i += sp.chunk) {
      if (abort.aborted) return;
      (target as Text).textContent += text.slice(i, i + sp.chunk);
      await sleep(sp.delay);
    }
    return;
  }
  if (source.nodeType !== Node.ELEMENT_NODE) return;
  if (isAtomic(source as Element)) {
    (target as Element).replaceWith(source.cloneNode(true));
    await sleep(Math.min(40, sp.delay * 3));
    return;
  }
  for (const child of Array.from(source.childNodes)) {
    if (abort.aborted) return;
    const clone = cloneShallow(child);
    target.appendChild(clone);
    await streamNode(child, clone, abort, sp);
  }
}

export function LessonStream({
  html,
  speed,
  ctx,
  instant,
}: {
  html: string;
  speed?: number;
  ctx?: Ctx;
  /** Sofort anzeigen statt streamen (Web: Punkt-Sprung/Zurück, Projektschritte). */
  instant?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = ref.current;
    if (!target) return;
    const abort = { aborted: false };
    const safe =
      sanitizeHtml(filterTrainerHtml(html)) ||
      "<p>(Für dieses Kapitel liegt noch kein Lesetext vor.)</p>";

    const emit = (eventType: string, itemId: string, itemType: string) => {
      if (ctx)
        sendProgressEvent({ courseId: ctx.courseId, moduleId: ctx.moduleId, blockId: ctx.blockId, itemId, itemType, eventType });
    };

    if (instant || prefersReducedMotion()) {
      target.innerHTML = safe;
      return;
    }

    // Endhöhe reservieren, dann leeren und streamen.
    target.innerHTML = safe;
    const reserved = target.offsetHeight;
    if (reserved) target.style.minHeight = `${reserved}px`;
    target.innerHTML = "";

    const doc = new DOMParser().parseFromString(`<div>${safe}</div>`, "text/html");
    const nodes = Array.from(doc.body.firstElementChild!.childNodes);
    const sp = streamSpeed(speed);

    emit("chapter_stream_started", "chapter", "chapter");
    (async () => {
      for (const node of nodes) {
        if (abort.aborted) break;
        const clone = cloneShallow(node);
        target.appendChild(clone);
        await streamNode(node, clone, abort, sp);
      }
      if (!abort.aborted) {
        target.style.minHeight = "";
        emit("chapter_stream_completed", "chapter-stream", "chapter_stream");
      }
    })();

    return () => {
      abort.aborted = true;
    };
  }, [html]);

  // Klick-Delegation für die ins HTML gebackenen Buttons (Frag-die-KI, Kopieren,
  // Schritt-Karussell, Material). GETRENNT vom Streaming-Effekt und nur einmal pro
  // Kapitel gebunden — der <article>-Ref ist stabil (Komponente ist je Kapitel
  // gekeyt). askQuestion/insertIntoCoach lesen den Kontext aus dem Coach-Store.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onClick = (ev: MouseEvent) => {
      handleLessonClick(ev, { askQuestion, insertIntoCoach, openPromptDialog, toast: showToast });
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);

  return <article class="lesson glass lesson-stream" ref={ref} />;
}
